/**
 * The lockstep turn loop.
 *
 * The contract, and the reason multiplayer works without sending state:
 *
 *   - The simulation only advances inside `runTurn`, once per turn, in order.
 *   - A turn cannot run until every player's commands for it have arrived.
 *   - Commands issued now are scheduled `commandDelay` turns into the future,
 *     which is the budget for a round trip through the relay.
 *
 * The loop is paced against the wall clock but is allowed to fall behind and
 * catch up, because the alternative (skipping a turn) would desync instantly.
 */

import { TICK_MS } from './constants.js';
import type { Transport } from '../net/transport.js';

/** How many turns behind schedule before the game reports itself as stalled. */
const STALL_TURNS = 8;
/** Cap on catch-up work in one frame, so a long pause cannot freeze the tab. */
const MAX_CATCHUP_TURNS = 6;

export interface LockstepHooks {
  /** Runs exactly one simulation tick with the merged command buffer. */
  simulate(turn: number, commands: Uint8Array): void;
  /** Produces the local player's commands for a future turn. */
  collectCommands(forTurn: number): Uint8Array;
  /** State hash of the most recently simulated turn. */
  checksum(): bigint;
  /** Called when the loop starts or stops waiting on the network. */
  onStallChanged?(stalled: boolean): void;
}

export class Lockstep {
  /** Turns simulated so far; also the turn number to run next. */
  private nextTurn = 0;
  /** Highest turn the local player has published commands for. */
  private submittedThrough = -1;
  private ready = new Map<number, Uint8Array>();
  private accumulator = 0;
  private running = false;
  private stalled = false;
  private lastTurnTime = 0;

  private transport: Transport;
  private hooks: LockstepHooks;

  constructor(transport: Transport, hooks: LockstepHooks) {
    this.transport = transport;
    this.hooks = hooks;
    this.transport.onTurn((turn, perPlayer) => {
      this.ready.set(turn, mergeCommands(perPlayer));
    });
  }

  get turn(): number {
    return this.nextTurn;
  }

  get isStalled(): boolean {
    return this.stalled;
  }

  /**
   * Primes the command pipeline. Every player must publish empty commands for
   * the first `commandDelay` turns, otherwise turn 0 could never be released.
   */
  start(now: number): void {
    const delay = this.transport.settings.commandDelay;
    for (let t = 0; t < delay; t++) {
      this.transport.submit(t, new Uint8Array(0), 0n);
      this.submittedThrough = t;
    }
    this.running = true;
    this.lastTurnTime = now;
  }

  stop(): void {
    this.running = false;
  }

  /**
   * Advances the loop. Returns the interpolation factor between the last
   * simulated turn and the next one, for the renderer to smooth motion with.
   */
  update(now: number, dtMs: number): number {
    if (!this.running) return 0;

    this.accumulator += dtMs;
    let ran = 0;

    while (this.accumulator >= TICK_MS && ran < MAX_CATCHUP_TURNS) {
      const commands = this.ready.get(this.nextTurn);
      if (commands === undefined) break; // Waiting on the network.

      this.ready.delete(this.nextTurn);
      this.hooks.simulate(this.nextTurn, commands);
      this.nextTurn++;
      this.accumulator -= TICK_MS;
      ran++;
      this.lastTurnTime = now;

      // Publish commands for the turn `commandDelay` ahead of the one just run.
      const target = this.nextTurn + this.transport.settings.commandDelay - 1;
      if (target > this.submittedThrough) {
        this.submittedThrough = target;
        this.transport.submit(target, this.hooks.collectCommands(target), this.hooks.checksum());
      }
    }

    // If catch-up hit its cap there is still a backlog, so do not let the
    // accumulator grow without bound and spiral.
    if (this.accumulator > TICK_MS * STALL_TURNS) {
      this.accumulator = TICK_MS * STALL_TURNS;
    }

    const stalledNow = this.ready.get(this.nextTurn) === undefined && now - this.lastTurnTime > TICK_MS * STALL_TURNS;
    if (stalledNow !== this.stalled) {
      this.stalled = stalledNow;
      this.hooks.onStallChanged?.(stalledNow);
    }

    return Math.min(1, Math.max(0, this.accumulator / TICK_MS));
  }
}

function mergeCommands(perPlayer: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of perPlayer) total += c.length;
  const out = new Uint8Array(total);
  let off = 0;
  // Fixed player order: every client must feed the simulation identical bytes.
  for (const c of perPlayer) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}
